import { api } from "../api";
import { ApiResponse } from "@/types/sports";
import { User, Subscription } from "@/types/user";

/**
 * Service for user and auth-related operations
 */
export class UserService {
  /**
   * Get the current user's profile
   */
  async getCurrentUser(): Promise<ApiResponse<User>> {
    try {
      console.log("UserService: Fetching current user from /api/auth/me");
      
      // Use the Auth0 /api/auth/me endpoint
      const response = await fetch('/api/auth/me');
      
      console.log("UserService: Response status:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("UserService: Failed Auth0 response:", {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        
        return {
          success: false,
          error: `Failed to fetch user data: ${response.status} ${response.statusText}`,
          data: null as unknown as User,
        };
      }

      const auth0User = await response.json();
      console.log("UserService: Auth0 user data:", auth0User);
      
      if (!auth0User || !auth0User.sub) {
        console.error("UserService: Invalid Auth0 user data - missing sub property", auth0User);
        return {
          success: false,
          error: "User is not authenticated",
          data: null as unknown as User,
        };
      }

      // Transform Auth0 user to our User model
      const user: User = {
        id: auth0User.sub,
        name: auth0User.name || auth0User.nickname || '',
        email: auth0User.email || '',
        profileImage: auth0User.picture || '',
        joinDate: new Date().toISOString(),
        preferences: {
          darkMode: true,
          emailNotifications: true,
          pushNotifications: false,
        },
      };

      console.log("UserService: Transformed user:", user);
      
      return {
        success: true,
        data: user,
      };
    } catch (error) {
      console.error("UserService: Error fetching user:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
        data: null as unknown as User,
      };
    }
  }

  /**
   * Update the current user's profile
   */
  async updateUserProfile(userData: Partial<User>): Promise<ApiResponse<User>> {
    return api.put<User>("/user", userData);
  }

  /**
   * Get the current user's subscription
   */
  async getUserSubscription(): Promise<ApiResponse<Subscription>> {
    try {
      // First check if the user is authenticated by trying to get the current user
      console.log("UserService.getUserSubscription: Checking authentication");
      const userResponse = await this.getCurrentUser();
      
      console.log("UserService.getUserSubscription: User check result", userResponse);
      
      // If the user is not authenticated, don't make the subscription request
      if (!userResponse.success) {
        console.log("UserService.getUserSubscription: User not authenticated");
        return {
          success: false,
          error: "User is not authenticated",
          data: null as unknown as Subscription,
        };
      }
      
      // User is authenticated, proceed with the subscription request
      console.log("UserService.getUserSubscription: Making API request to /subscriptions");
      const response = await api.get<Subscription>("/subscriptions");
      console.log("UserService.getUserSubscription: API response", response);
      return response;
    } catch (error) {
      console.error("UserService.getUserSubscription: Error", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch subscription",
        data: null as unknown as Subscription,
      };
    }
  }

  /**
   * Upgrade to a pro or elite subscription
   */
  async upgradeToPro(
    paymentDetails: { tier: "pro" | "elite" } & Record<string, unknown>
  ): Promise<ApiResponse<Subscription>> {
    return api.post<Subscription>("/subscriptions/upgrade", paymentDetails);
  }

  /**
   * Cancel the current subscription
   */
  async cancelSubscription(): Promise<ApiResponse<Subscription>> {
    return api.post<Subscription>("/subscriptions/cancel", {});
  }

  /**
   * Update user preferences
   */
  async updatePreferences(
    preferences: Partial<User["preferences"]>
  ): Promise<ApiResponse<User>> {
    return api.put<User>("/user/preferences", preferences);
  }
}

// Create a singleton instance for use throughout the app
export const userService = new UserService();
